import { useState } from 'react'
import { GRACE_MAX_S, graceText, parseGrace } from './grace'
import { Link } from 'react-router'
import { useSession } from '../session'
import { BILLING_PATH, graceCeiling, secondsText } from '../billing'
import { t } from '../i18n'
import { withMono } from '../mono'

/**
 * "Hold when OBS drops" (source grace, D21) of a stream key or a destination:
 * inherit (the default / as the stream key) or an own value in whole seconds,
 * 0–600, checked here before saving. What is in force now is shown next to it,
 * and what will be after saving when the form differs. The parent saves.
 */
export function GraceSettings({ value, saved, inherited, inheritLabel, inheritFrom, subject, live, disabled, onChange, testId = 'grace' }: {
  /** the form's choice, ms; null = inherit */
  value: number | null
  /** what is saved, ms; null = inherit */
  saved: number | null
  /** what inheriting gives now, ms; undefined = unknown (an older center does not say) */
  inherited?: number
  /** "Default" (a key) or "As the stream key" (a destination) */
  inheritLabel: string
  /** where the inherited value comes from, in words: "the center's default", "the stream key's" */
  inheritFrom: string
  /** "the platforms" (a key) or "YouTube" (a destination) */
  subject: string
  live?: boolean
  disabled?: boolean
  /** a new choice; invalid = the typed seconds are not 0–600 (the parent keeps Save off) */
  onChange: (v: number | null, invalid: boolean) => void
  testId?: string
}) {
  const own = value !== null
  const [text, setText] = useState(() => String(Math.round((value ?? inherited ?? 10_000) / 1000)))
  const parsed = own ? parseGrace(text) : {}
  const error = own ? parsed.error : undefined
  const inheritedText = inherited === undefined ? inheritFrom : graceText(inherited)
  const now = saved ?? inherited
  const nowFrom = saved !== null ? 'own value' : inheritFrom
  const after = own ? parsed.ms : inherited
  const dirty = value !== saved && !error
  // DS: PL-04 — the plan's ceiling (source_grace_max_s, design §4.1): a hint, and a warning above it — the center
  // cuts a longer hold to it (and refuses the value once the check enforces)
  const { user } = useSession()
  const ceil = graceCeiling(user)
  const overPlan = ceil !== undefined && own && parsed.ms !== undefined && parsed.ms > ceil.s * 1000

  const pick = (o: boolean) => {
    if (o === own) return
    if (!o) return onChange(null, false)
    const p = parseGrace(text)
    onChange(p.ms ?? 0, !!p.error)
  }

  return (
    <div data-testid={testId} data-own={own} className="flex flex-col gap-2.5 rounded-md border border-line p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-medium text-text">Hold when OBS drops</span>
        {own ? (
          <button type="button" data-testid={`${testId}-reset`} disabled={disabled} onClick={() => pick(false)}
            className="min-h-[44px] text-xs text-muted underline-offset-4 hover:text-text hover:underline md:min-h-8">
            {inheritLabel === 'Default' ? 'Use the default' : 'Use the stream key’s'}
          </button>
        ) : (
          <span data-testid={`${testId}-inherit-tag`} className="font-mono text-[10px] tracking-[.06em] text-muted">{inheritLabel.toUpperCase()}</span>
        )}
      </div>
      <p className="text-[11px] text-muted">
        If OBS disconnects, {subject} stay{subject.startsWith('the ') ? '' : 's'} connected this long. OBS back within it continues the same broadcast; after it the live ends there.
      </p>
      {ceil && (
        <p data-testid={`${testId}-plan`} data-over={overPlan} className={`flex flex-wrap items-center gap-x-2 text-[11px] ${overPlan ? 'text-warn' : 'text-muted'}`}>
          <span>{withMono(overPlan ? t('plan.graceOver', { plan: ceil.plan, max: secondsText(ceil.s) }) : t('plan.graceCeiling', { plan: ceil.plan, max: secondsText(ceil.s) }))}</span>
          <Link to={BILLING_PATH} className="text-accent no-underline">{t('plan.seePlan')} →</Link>
        </p>
      )}

      <div role="radiogroup" aria-label="Hold when OBS drops" className="grid grid-cols-2 gap-1.5">
        <button type="button" role="radio" aria-checked={!own} data-testid={`${testId}-inherit`} disabled={disabled} onClick={() => pick(false)}
          className={`flex min-h-[44px] flex-col items-start justify-center gap-0.5 rounded-md border px-2.5 py-1.5 text-left ${!own ? 'border-live/50 bg-live/[.12]' : 'border-line-2 hover:border-muted'}`}>
          <span className={`text-[13px] font-medium ${!own ? 'text-live' : 'text-text'}`}>{inheritLabel}</span>
          <span className="tabular font-mono text-[11px] text-muted">{inheritedText}</span>
        </button>
        <button type="button" role="radio" aria-checked={own} data-testid={`${testId}-own`} disabled={disabled} onClick={() => pick(true)}
          className={`flex min-h-[44px] flex-col items-start justify-center gap-0.5 rounded-md border px-2.5 py-1.5 text-left ${own ? 'border-live/50 bg-live/[.12]' : 'border-line-2 hover:border-muted'}`}>
          <span className={`text-[13px] font-medium ${own ? 'text-live' : 'text-text'}`}>Own value</span>
          <span className="text-[11px] text-muted">0 to {GRACE_MAX_S} s</span>
        </button>
      </div>

      {own && (
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-muted">Seconds</span>
          <span className={`flex min-h-[44px] items-center gap-1.5 rounded-md border bg-bg px-2.5 md:min-h-[38px] ${error ? 'border-fail shadow-error' : 'border-line-2 focus-within:border-accent focus-within:shadow-focus'}`}>
            <input
              data-testid={`${testId}-seconds`}
              inputMode="numeric"
              autoComplete="off"
              value={text}
              disabled={disabled}
              aria-invalid={!!error}
              aria-describedby={error ? `${testId}-error` : undefined}
              onChange={(e) => {
                setText(e.target.value)
                const p = parseGrace(e.target.value)
                onChange(p.ms ?? value ?? 0, !!p.error)
              }}
              className="tabular min-w-0 flex-1 bg-transparent font-mono text-sm text-text outline-none"
            />
            <span className="font-mono text-xs text-muted" aria-hidden>s</span>
          </span>
          {error && <span id={`${testId}-error`} data-testid={`${testId}-error`} role="alert" className="text-[11px] text-fail">✕ {error}</span>}
        </label>
      )}

      <p className="num text-[11px] text-text-soft" data-testid={`${testId}-effective`}>
        In force: <b className="font-mono font-medium text-text">{now === undefined ? inheritFrom : graceText(now)}</b>
        {now !== undefined && <span className="text-muted"> · {nowFrom}</span>}
        {dirty && after !== undefined && (
          <span data-testid={`${testId}-after`}>
            {' '}→ after saving <b className="font-mono font-medium text-text">{graceText(after)}</b>
          </span>
        )}
      </p>
      {live && <p className="text-[11px] text-muted">You’re live: the new value counts from the next time OBS drops. A hold already counting goes on.</p>}
    </div>
  )
}
